import type {
  AisleDefinition,
  AisleWalkthrough,
  CampaignPlacement,
  CampaignPhase,
  ComplianceStatus,
  FloorplanZone,
  MediaFormatType,
  MediaLocation,
  OpeningHours,
  StoreContact,
  StoreImage,
  StoreFormatOperatorSummary,
  StoreProfileDetail,
  StoreReferenceImage,
  StoreSurvey,
  UnmappedEvidenceItem,
  WalkthroughMarker,
  GalleryImageMetaEntry,
} from '../types';
import { deriveTwinReadiness } from './twinReadiness';
import { computeEvidenceMappingStats, countStoreAreas } from './spatialEntity';
import { deriveCampaignPhase } from './campaignPhase';
import { mergeZonesWithInstoreLocations, zoneLabelById } from './floorplanZoneUtils';
import { buildZoneByInstoreLocationId, resolveZoneIdForInstoreLocationIds } from './instoreLocationGroups';
import { resolveStoreInstoreLocations } from './inStoreLocationLabels';
import { normalizeVideoRotationDegrees } from './walkthroughVideoRotation';

/** Merge saved gallery meta with posting-instruction location when the user has not overridden tags. */
export function resolveImageInstoreLocationIds(
  image: { postingInstructionInstoreLocationId?: number },
  meta?: GalleryImageMetaEntry,
): number[] {
  if (meta?.instoreLocationIds !== undefined) {
    return meta.instoreLocationIds;
  }
  const autoId = image.postingInstructionInstoreLocationId;
  return autoId != null ? [autoId] : [];
}

/** Store locations explicitly selected by an admin plus any required by campaigns. */
export function resolveApplicableInstoreLocationIds(
  savedIds: number[],
  campaigns: Array<{ instoreLocationId?: number | null }>,
): number[] {
  return Array.from(
    new Set([
      ...savedIds,
      ...campaigns
        .map((campaign) => campaign.instoreLocationId)
        .filter((id): id is number => id != null),
    ]),
  );
}

export interface LiveMediaRequirement {
  id: number;
  mediaFormatId: number;
  mediaFormatName: string;
  quantity: number;
  notes?: string | null;
  dimensions?: string | null;
  formatType?: string | null;
  photoUrl?: string | null;
  floorplanAisleId?: string | null;
  floorplanZoneId?: string | null;
  updatedAt?: string | null;
}

export interface LivePackTemplate {
  mediaFormatId: number;
  mediaFormatName: string;
  quantity: number;
}

export interface LiveStorePack {
  id: number;
  name: string;
  description?: string | null;
  isActive?: boolean | null;
  templates: LivePackTemplate[];
}

export interface LiveStoreAssignment {
  id: number;
  campaignId: number;
  /** campaign_tasks.id — same id returned as gallery evidence assignmentId */
  assignmentId?: number | null;
  campaignName: string;
  packId: number;
  packName: string;
  cycleId: number;
  cycleName?: string | null;
  instoreLocationId?: number | null;
  instoreLocationName?: string | null;
  postingInstructionsText?: string | null;
  postingInstructionsImageUrl?: string | null;
  mediaRequirements?: unknown;
  sentAt?: string | null;
  status: string;
}

interface LiveAssignmentMediaRequirement {
  formatId?: number | string | null;
  mediaFormatId?: number | string | null;
  formatName?: string | null;
  mediaFormatName?: string | null;
  name?: string | null;
  quantity?: number | string | null;
  expectedQuantity?: number | string | null;
}

export interface LiveInstoreLocation {
  id: number;
  name: string;
}

export interface RetailerInstoreLocation {
  id: number;
  name: string;
}

export interface LiveWalkthroughRecord {
  id: number;
  storeId: number;
  title: string;
  routeDescription?: string | null;
  videoPath: string;
  videoUrl: string;
  thumbnailPath?: string | null;
  thumbnailUrl?: string | null;
  durationSeconds?: number | null;
  videoRotationDegrees?: number | null;
  aisleNumbers: number[];
  /** Canonical floorplan aisle links */
  floorplanAisleIds?: string[];
  floorplanAisleAssociations?: Array<{ id: string; resolved: boolean }>;
  instoreLocationId?: number | null;
  instoreLocationIds?: number[];
  tags: string[];
  timelineMarkers: Array<{
    id: string;
    timestampSeconds: number;
    label: string;
    tags?: string[];
  }>;
  capturedByName?: string | null;
  captureDate?: string | null;
  createdAt?: string | null;
}

export interface LiveFloorplanRecord {
  aisles: AisleDefinition[];
  zones: FloorplanZone[];
  imagePath?: string | null;
  imageUrl?: string | null;
  updatedAt?: string | null;
  imageZoneTags: Record<string, string>;
  galleryImageMeta: Record<string, GalleryImageMetaEntry>;
  twinPublishedAt?: string | null;
  twinPublishedByName?: string | null;
}

export interface LiveSurveyRecord {
  id: number;
  storeId: number;
  title: string;
  surveyDate: string;
  status: string;
  notes: string | null;
  storeVisitId: number | null;
  createdByUserId: number | null;
  createdByName: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface LiveReferenceImageRecord {
  id: number;
  storeId: number;
  sourceType: 'store_reference';
  title: string;
  description: string | null;
  imageUrl: string;
  thumbnailUrl: string | null;
  captureDate: string | null;
  category: string;
  floorplanAisleId: string | null;
  floorplanAisleResolved: boolean | null;
  floorplanZoneId: string | null;
  floorplanZoneResolved: boolean | null;
  instoreLocationId: number | null;
  tags: string[];
  uploadedByName: string | null;
}

export interface LiveOverviewRecord {
  storeFormatLabel?: string | null;
  salesAreaSqm?: number | null;
  totalFloorAreaSqm?: number | null;
  ceilingHeightMetres?: number | null;
  checkoutCount?: number | null;
  openingHours: OpeningHours[];
  contacts: StoreContact[];
  accessNotes?: string | null;
  parkingNotes?: string | null;
  loadingNotes?: string | null;
  deliveryNotes?: string | null;
  /** Locations from the retailer catalog enabled for this store */
  instoreLocationIds?: number[];
}

export interface LiveStoreContext {
  storeId: number;
  mediaRequirements: LiveMediaRequirement[];
  formatOperatorAssignments?: StoreFormatOperatorSummary[];
  packs: LiveStorePack[];
  assignments: LiveStoreAssignment[];
  instoreLocations: LiveInstoreLocation[];
  walkthroughs: LiveWalkthroughRecord[];
  floorplan?: LiveFloorplanRecord | null;
  overview?: LiveOverviewRecord | null;
  surveys?: LiveSurveyRecord[];
  referenceImages?: LiveReferenceImageRecord[];
  retailerInstoreLocations: RetailerInstoreLocation[];
  stats: {
    mediaFormatCount: number;
    totalMediaCapacity: number;
    packCount: number;
    activeAssignmentCount: number;
    activeCampaignCount?: number;
    campaignsToDateCount?: number;
    campaignCount: number;
    instoreLocationCount: number;
    walkthroughCount: number;
    surveyCount?: number;
    referenceImageCount?: number;
  };
}

function inferMediaFormatType(name: string): MediaFormatType {
  const n = name.toLowerCase();
  if (n.includes('aisle fin') || n.includes('aisle-fin')) return 'aisle_fin';
  if (n.includes('shelf talker') || n.includes('shelf-talker')) return 'shelf_talker';
  if (n.includes('floor graphic') || n.includes('floor sticker')) return 'floor_graphic';
  if (n.includes('fridge') || n.includes('chiller')) return 'fridge_vinyl';
  if (n.includes('entrance') || n.includes('foyer')) return 'entrance_media';
  if (n.includes('trolley') || n.includes('cart')) return 'trolley_media';
  if (n.includes('checkout') || n.includes('till')) return 'checkout_media';
  if (n.includes('promo') || n.includes('bay') || n.includes('end')) return 'promotional_bay';
  return 'promotional_bay';
}

function parseAisleNumber(name: string, fallback: number): number {
  const match = name.match(/aisle\s*(\d+)/i);
  return match ? parseInt(match[1], 10) : fallback;
}

export function mapLiveMediaRequirements(requirements: LiveMediaRequirement[]): MediaLocation[] {
  return requirements.map((req, index) => {
    const col = index % 6;
    const row = Math.floor(index / 6);
    return {
      id: `live-smr-${req.id}`,
      format: inferMediaFormatType(req.mediaFormatName),
      formatLabel: req.mediaFormatName,
      locationLabel: req.notes?.trim() || `Store capacity — ${req.mediaFormatName}`,
      zone: 'Store profile',
      capacity: req.quantity,
      dimensions: req.dimensions ?? '—',
      availability: 'available',
      condition: 'good',
      verificationImageUrl: req.photoUrl ?? undefined,
      lastSurveyedDate: req.updatedAt?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
      floorplanX: 10 + col * 14,
      floorplanY: 20 + row * 12,
      mediaFormatId: req.mediaFormatId,
      liveRequirementId: req.id,
      floorplanAisleId: req.floorplanAisleId ?? null,
      floorplanZoneId: req.floorplanZoneId ?? null,
      dataSource: 'live',
    };
  });
}

function assignmentPhase(status: string, installationDate: string, expectedRemovalDate?: string): CampaignPhase {
  return deriveCampaignPhase({ assignmentStatus: status, installationDate, expectedRemovalDate });
}

function assignmentCompliance(status: string): ComplianceStatus {
  if (status === 'operator_complete') return 'compliant';
  if (status === 'sent_to_operator') return 'pending';
  return 'unavailable';
}

export function mapLiveAssignments(
  assignments: LiveStoreAssignment[],
  mediaLocations: MediaLocation[],
  aisles: AisleDefinition[],
  zoneByInstoreLocationId: Map<number, FloorplanZone>,
): CampaignPlacement[] {
  const placementByCampaignLocation = new Map<string, CampaignPlacement>();

  for (const row of assignments) {
    const linkedZone = row.instoreLocationId != null
      ? zoneByInstoreLocationId.get(row.instoreLocationId)
      : undefined;
    // Only link to a drawn aisle when the instore location maps to a floorplan shape.
    const aisle = linkedZone
      ? undefined
      : aisles.find(
        (a) => row.instoreLocationName && a.name.toLowerCase() === row.instoreLocationName.toLowerCase(),
      );

    const rawRequirements = Array.isArray(row.mediaRequirements)
      ? row.mediaRequirements as LiveAssignmentMediaRequirement[]
      : [];
    // An assignment is the campaign/store task; the retailer-facing placement
    // unit is each media format inside that task.
    const requirements = rawRequirements.length > 0
      ? rawRequirements
      : [{ formatName: row.packName, quantity: 1 }];

    const formatsByKey = new Map<string, NonNullable<CampaignPlacement['mediaFormats']>[number]>();
    requirements.forEach((requirement) => {
      const rawFormatId = requirement.mediaFormatId ?? requirement.formatId;
      const numericFormatId = rawFormatId != null && Number.isFinite(Number(rawFormatId))
        ? Number(rawFormatId)
        : null;
      const formatLabel = requirement.mediaFormatName?.trim()
        || requirement.formatName?.trim()
        || requirement.name?.trim()
        || row.packName;
      const rawQuantity = requirement.quantity ?? requirement.expectedQuantity ?? 1;
      const plannedQuantity = Number.isFinite(Number(rawQuantity))
        ? Math.max(0, Number(rawQuantity))
        : 1;
      const formatKey = numericFormatId != null
        ? `id-${numericFormatId}`
        : `name-${formatLabel.toLowerCase()}`;
      const existingFormat = formatsByKey.get(formatKey);
      formatsByKey.set(formatKey, {
        mediaFormatId: numericFormatId,
        name: formatLabel,
        quantity: Math.max(existingFormat?.quantity ?? 0, plannedQuantity),
      });
    });

    const mediaFormats = [...formatsByKey.values()];
    const primaryFormat = mediaFormats[0] ?? {
      mediaFormatId: null,
      name: row.packName,
      quantity: 1,
    };
    const placementKey = `${row.campaignId}-${row.instoreLocationId ?? 'none'}`;
    const existingPlacement = placementByCampaignLocation.get(placementKey);

    if (existingPlacement) {
      const mergedFormats = new Map(
        (existingPlacement.mediaFormats ?? []).map((format) => [
          format.mediaFormatId != null
            ? `id-${format.mediaFormatId}`
            : `name-${format.name.toLowerCase()}`,
          format,
        ]),
      );
      for (const format of mediaFormats) {
        const key = format.mediaFormatId != null
          ? `id-${format.mediaFormatId}`
          : `name-${format.name.toLowerCase()}`;
        const existing = mergedFormats.get(key);
        mergedFormats.set(key, {
          ...format,
          quantity: Math.max(existing?.quantity ?? 0, format.quantity),
        });
      }
      placementByCampaignLocation.set(placementKey, {
        ...existingPlacement,
        mediaFormats: [...mergedFormats.values()],
        plannedQuantity: [...mergedFormats.values()].reduce(
          (total, format) => total + format.quantity,
          0,
        ),
      });
      continue;
    }

    const linkedMedia = mediaLocations.find((media) =>
      (primaryFormat.mediaFormatId != null && media.mediaFormatId === primaryFormat.mediaFormatId)
      || media.formatLabel.toLowerCase() === primaryFormat.name.toLowerCase(),
    );
    const plannedQuantity = mediaFormats.reduce((total, format) => total + format.quantity, 0);

    placementByCampaignLocation.set(placementKey, {
        id: `live-campaign-${row.campaignId}-location-${row.instoreLocationId ?? 'none'}`,
        campaignId: String(row.campaignId),
        campaignName: row.campaignName,
        advertiser: row.campaignName.split('—')[0]?.trim() || row.campaignName,
        brand: row.packName,
        cycleCode: row.cycleName ?? `Cycle ${row.cycleId}`,
        mediaLocationId: linkedMedia?.id ?? `live-campaign-${row.campaignId}`,
        mediaFormatId: primaryFormat.mediaFormatId,
        mediaFormats,
        format: inferMediaFormatType(primaryFormat.name),
        formatLabel: primaryFormat.name,
        // Attach to one canonical spatial entity — drawn zone/aisle only, never a department label alone.
        aisleId: aisle?.id ?? 'unknown',
        aisleNumber: aisle?.number ?? 0,
        instoreLocationId: row.instoreLocationId ?? null,
        instoreLocationName: row.instoreLocationName ?? null,
        floorplanZoneId: linkedZone?.id ?? null,
        campaignTaskId: row.assignmentId ?? null,
        plannedQuantity,
        installedQuantity: row.status === 'operator_complete' ? plannedQuantity : 0,
        complianceStatus: assignmentCompliance(row.status),
        installationDate: row.sentAt?.slice(0, 10) ?? '—',
        expectedRemovalDate: '—',
        installationImageUrls: row.postingInstructionsImageUrl ? [row.postingInstructionsImageUrl] : [],
        phase: assignmentPhase(row.status, row.sentAt?.slice(0, 10) ?? '—'),
        assignmentStatus: row.status,
        dataSource: 'live',
        postingInstructionsText: row.postingInstructionsText ?? undefined,
      });
  }

  return [...placementByCampaignLocation.values()];
}

const MISSING_WALKTHROUGH_THUMB =
  'data:image/svg+xml,' + encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360">
      <rect width="640" height="360" fill="#111827"/>
      <text x="320" y="180" fill="#9CA3AF" font-family="system-ui,sans-serif" font-size="18" text-anchor="middle">No thumbnail yet</text>
    </svg>`,
  );

export function mapLiveWalkthroughs(records: LiveWalkthroughRecord[]): AisleWalkthrough[] {
  return records.map((row) => {
    const hasRealThumbnail = Boolean(row.thumbnailUrl || row.thumbnailPath);
    const instoreLocationIds = row.instoreLocationIds?.length
      ? row.instoreLocationIds
      : (row.instoreLocationId != null ? [row.instoreLocationId] : []);
    const durationSeconds = row.durationSeconds != null && row.durationSeconds > 0
      ? row.durationSeconds
      : null;
    return {
      id: `live-walk-${row.id}`,
      liveWalkthroughId: row.id,
      title: row.title,
      aisleNumbers: row.aisleNumbers,
      floorplanAisleIds: row.floorplanAisleIds ?? [],
      instoreLocationIds,
      routeDescription: row.routeDescription?.trim() || 'Uploaded store walk-through',
      captureDate: row.captureDate ?? row.createdAt?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
      durationSeconds,
      videoRotationDegrees: normalizeVideoRotationDegrees(row.videoRotationDegrees ?? undefined),
      surveyId: 'store-profile-upload',
      capturedBy: row.capturedByName ?? 'Admin',
      processingStatus: hasRealThumbnail ? ('processed' as const) : ('processing' as const),
      identifiedPlacements: row.timelineMarkers.length,
      hasRealThumbnail,
      thumbnailUrl: hasRealThumbnail
        ? (row.thumbnailUrl as string)
        : MISSING_WALKTHROUGH_THUMB,
      videoUrl: row.videoUrl,
      videoStoragePath: row.videoPath,
      tags: row.tags,
      dataSource: 'live' as const,
      timelineMarkers: row.timelineMarkers.map((marker): WalkthroughMarker => ({
        id: marker.id,
        timestampSeconds: marker.timestampSeconds,
        campaignId: 'profile-marker',
        campaignName: marker.label,
        brand: marker.tags?.join(', ') ?? 'Tagged',
        formatLabel: 'Walk-through marker',
        complianceStatus: 'unavailable',
        label: marker.label,
        tags: marker.tags,
      })),
    };
  });
}

function mapLiveSurveys(records: LiveSurveyRecord[]): StoreSurvey[] {
  return records.map((row) => ({
    id: row.id,
    title: row.title,
    surveyDate: row.surveyDate,
    status: row.status,
    notes: row.notes,
    storeVisitId: row.storeVisitId,
    createdByName: row.createdByName,
    createdAt: row.createdAt,
  }));
}

function mapLiveReferenceImages(records: LiveReferenceImageRecord[]): StoreReferenceImage[] {
  return records.map((row) => ({
    id: row.id,
    title: row.title,
    description: row.description,
    imageUrl: row.imageUrl,
    thumbnailUrl: row.thumbnailUrl,
    captureDate: row.captureDate,
    category: row.category,
    floorplanAisleId: row.floorplanAisleId,
    floorplanAisleResolved: row.floorplanAisleResolved,
    floorplanZoneId: row.floorplanZoneId,
    floorplanZoneResolved: row.floorplanZoneResolved,
    instoreLocationId: row.instoreLocationId,
    tags: row.tags,
    uploadedByName: row.uploadedByName,
  }));
}

function enrichCampaignsWithGalleryEvidence(
  campaigns: CampaignPlacement[],
  images: StoreImage[],
): CampaignPlacement[] {
  return campaigns.map((campaign) => {
    const matched = images.filter((image) => {
      if (image.hidden) return false;
      if (campaign.campaignTaskId != null && image.assignmentId === campaign.campaignTaskId) return true;
      if (campaign.campaignId && image.campaignId != null && String(image.campaignId) === campaign.campaignId) {
        if (campaign.instoreLocationId != null) {
          return image.instoreLocationIds?.includes(campaign.instoreLocationId)
            || image.postingInstructionInstoreLocationId === campaign.instoreLocationId;
        }
        return image.caption.toLowerCase().includes(campaign.campaignName.toLowerCase());
      }
      return false;
    });

    const galleryUrls = matched.map((image) => image.assetUrl || image.url).filter(Boolean);
    const urls = [...new Set([...campaign.installationImageUrls, ...galleryUrls])];
    const imageDates = matched.map((image) => image.captureDate).filter(Boolean).sort();
    const latestImageDate = imageDates[imageDates.length - 1] ?? campaign.latestImageDate;

    return {
      ...campaign,
      installationImageUrls: urls,
      latestImageDate,
    };
  });
}

function parseAisleNumberFromName(name: string): number | undefined {
  const match = name.match(/aisle\s*(\d+)/i);
  return match ? parseInt(match[1], 10) : undefined;
}

export function applyLiveStoreContext(
  profile: StoreProfileDetail,
  liveContext?: LiveStoreContext,
): StoreProfileDetail {
  if (!liveContext) {
    const evidenceMappingStats = computeEvidenceMappingStats(profile);
    return {
      ...profile,
      storeAreasCount: countStoreAreas(profile),
      evidenceMappingStats,
      twinReadiness: deriveTwinReadiness({
        hasStoreIdentity: Boolean(profile.storeName && profile.address && profile.retailer),
        hasFloorplanImage: false,
        aisleCount: profile.aisles.length,
        zoneCount: profile.floorplanZones.length,
        evidenceCount: 0,
        pendingSuggestionCount: 0,
        unmappedEvidenceCount: 0,
        twinPublishedAt: null,
      }),
    };
  }

  const liveMedia = mapLiveMediaRequirements(liveContext.mediaRequirements);
  const savedFloorplan = liveContext.floorplan ?? null;
  const savedZones = savedFloorplan?.zones ?? [];
  const hasUploadedPlanImage = Boolean(savedFloorplan?.imagePath || savedFloorplan?.imageUrl);

  /**
   * Canonical aisles come ONLY from the saved floorplan. In-store locations from
   * posting instructions are hints (suggestions), never fabricated twin aisles —
   * this is what removes the "zero aisles but a walkthrough tagged Aisle 3"
   * contradiction: untagged-to-floorplan evidence surfaces in the unmapped inbox
   * instead of silently creating or hiding aisles.
   */
  const canonicalAisles: AisleDefinition[] = (savedFloorplan?.aisles ?? [])
    .map((aisle) => ({ ...aisle, dataSource: 'live' as const }));

  const retailerLocations = liveContext.retailerInstoreLocations ?? [];
  const mergedFloorplanZones = savedFloorplan
    ? mergeZonesWithInstoreLocations(
      savedZones.length > 0
        ? savedZones
        : hasUploadedPlanImage
          ? []
          : profile.floorplanZones,
      retailerLocations,
      { allowAutoScaffold: !hasUploadedPlanImage },
    )
    : profile.floorplanZones;

  const aisleById = new Map(canonicalAisles.map((aisle) => [aisle.id, aisle]));
  const aisleByName = new Map(canonicalAisles.map((aisle) => [aisle.name.toLowerCase(), aisle]));
  const locationNameById = new Map(retailerLocations.map((loc) => [loc.id, loc.name]));
  const zoneByInstoreLocationId = buildZoneByInstoreLocationId(mergedFloorplanZones);

  const liveCampaignsBase = mapLiveAssignments(
    liveContext.assignments,
    liveMedia,
    canonicalAisles,
    zoneByInstoreLocationId,
  );
  const liveWalkthroughs = mapLiveWalkthroughs(liveContext.walkthroughs ?? []);
  const liveSurveys = mapLiveSurveys(liveContext.surveys ?? []);
  const liveReferenceImages = mapLiveReferenceImages(liveContext.referenceImages ?? []);

  const mergedMedia = liveMedia.length > 0 ? liveMedia : profile.mediaLocations;
  const mergedWalkthroughs = liveWalkthroughs.length > 0
    ? liveWalkthroughs
    : profile.walkthroughs.filter((w) => w.dataSource !== 'live');

  const imageZoneTags = savedFloorplan?.imageZoneTags ?? {};
  const galleryImageMeta = savedFloorplan?.galleryImageMeta ?? {};

  const zoneById = new Map(mergedFloorplanZones.map((zone) => [zone.id, zone]));
  const mergedImages = profile.images.map((image) => {
    const meta = galleryImageMeta[image.id];
    const instoreLocationIds = resolveImageInstoreLocationIds(image, meta);
    const explicitZoneId = imageZoneTags[image.id] ?? null;
    const resolvedZoneId = explicitZoneId
      ?? resolveZoneIdForInstoreLocationIds(instoreLocationIds, zoneByInstoreLocationId)
      ?? null;
    const floorplanZoneId = resolvedZoneId;
    const zone = floorplanZoneId ? zoneById.get(floorplanZoneId) : undefined;

    return {
      ...image,
      sourceType: image.sourceType ?? ('campaign_proof' as const),
      dataSource: image.dataSource ?? ('live' as const),
      ...(floorplanZoneId
        ? {
            floorplanZoneId,
            floorplanZoneLabel: zone?.label ?? zoneLabelById(mergedFloorplanZones, floorplanZoneId) ?? undefined,
            instoreLocationId: zone?.instoreLocationId ?? instoreLocationIds[0] ?? undefined,
          }
        : {}),
      hidden: meta?.hidden ?? false,
      instoreLocationIds,
    };
  });

  const liveCampaigns = enrichCampaignsWithGalleryEvidence(liveCampaignsBase, mergedImages);

  const savedOverview = liveContext.overview;
  const mergedProfileBase = {
    storeFormat: savedOverview?.storeFormatLabel ?? profile.storeFormat,
    storeDimensions: {
      ...profile.storeDimensions,
      salesAreaSqm: savedOverview?.salesAreaSqm ?? profile.storeDimensions.salesAreaSqm,
      totalFloorAreaSqm: savedOverview?.totalFloorAreaSqm ?? profile.storeDimensions.totalFloorAreaSqm,
      ceilingHeightMetres: savedOverview?.ceilingHeightMetres ?? profile.storeDimensions.ceilingHeightMetres,
      checkoutCount: savedOverview?.checkoutCount ?? profile.storeDimensions.checkoutCount,
    },
    openingHours: savedOverview?.openingHours?.length
      ? savedOverview.openingHours
      : profile.openingHours,
    contacts: savedOverview?.contacts?.length
      ? savedOverview.contacts
      : profile.contacts,
    accessNotes: savedOverview?.accessNotes != null ? savedOverview.accessNotes : profile.accessNotes,
    parkingNotes: savedOverview?.parkingNotes != null ? savedOverview.parkingNotes : profile.parkingNotes,
    loadingNotes: savedOverview?.loadingNotes != null ? savedOverview.loadingNotes : profile.loadingNotes,
    deliveryNotes: savedOverview?.deliveryNotes != null ? savedOverview.deliveryNotes : profile.deliveryNotes,
  };

  /** "Active campaigns" — distinct campaigns with a live assignment, matching the campaigns view. */
  const activeCampaignCount = liveContext.stats.activeCampaignCount
    ?? new Set(
      liveContext.assignments
        .filter((a) => a.status === 'sent_to_operator')
        .map((a) => a.campaignId),
    ).size;
  /** "Campaigns to date" — every campaign ever linked. Never presented as current. */
  const campaignsToDateCount = liveContext.stats.campaignsToDateCount
    ?? liveContext.stats.campaignCount
    ?? null;

  /** Last verified survey: latest non-deleted survey, falling back to the latest walkthrough. */
  const latestSurveyDate = liveSurveys.length
    ? liveSurveys.map((s) => s.surveyDate).sort().at(-1) ?? null
    : null;
  const latestWalkthroughDate = mergedWalkthroughs.length
    ? mergedWalkthroughs.map((w) => w.captureDate).sort().at(-1) ?? null
    : null;
  const lastSurveyDate = latestSurveyDate ?? latestWalkthroughDate;

  /** Media locations = distinct format rows; total capacity = summed quantities. */
  const mediaLocationCount = mergedMedia.length;
  const totalMediaCapacity = liveContext.stats.totalMediaCapacity
    || mergedMedia.reduce((sum, m) => sum + m.capacity, 0);

  /** Unmapped evidence: no canonical aisle/zone link. Pending: has hints awaiting a link. */
  const unmappedEvidence: UnmappedEvidenceItem[] = [];
  const pendingSuggestions: UnmappedEvidenceItem[] = [];

  for (const walk of mergedWalkthroughs) {
    const hasCanonicalLink = (walk.floorplanAisleIds?.length ?? 0) > 0;
    if (hasCanonicalLink) continue;
    const hintIds = walk.instoreLocationIds ?? [];
    const resolvedZoneId = resolveZoneIdForInstoreLocationIds(hintIds, zoneByInstoreLocationId);
    if (resolvedZoneId) continue;
    const hintLabel = hintIds.length ? locationNameById.get(hintIds[0]!) : undefined;
    const item: UnmappedEvidenceItem = {
      id: walk.id,
      kind: 'walkthrough',
      title: walk.title,
      captureDate: walk.captureDate,
      thumbnailUrl: walk.hasRealThumbnail ? walk.thumbnailUrl : null,
      liveId: walk.liveWalkthroughId,
      instoreLocationIds: hintIds,
      suggestedAisleNumber: hintLabel ? parseAisleNumberFromName(hintLabel) : (walk.aisleNumbers[0] ?? undefined),
      suggestedLocationLabel: hintLabel,
    };
    (hintIds.length > 0 ? pendingSuggestions : unmappedEvidence).push(item);
  }

  for (const image of liveReferenceImages) {
    const hasCanonicalLink = Boolean(image.floorplanAisleId || image.floorplanZoneId);
    if (hasCanonicalLink) continue;
    const hintLabel = image.instoreLocationId != null ? locationNameById.get(image.instoreLocationId) : undefined;
    const item: UnmappedEvidenceItem = {
      id: `live-refimg-${image.id}`,
      kind: 'reference_image',
      title: image.title,
      captureDate: image.captureDate ?? '',
      thumbnailUrl: image.thumbnailUrl ?? image.imageUrl,
      liveId: image.id,
      instoreLocationIds: image.instoreLocationId != null ? [image.instoreLocationId] : [],
      suggestedAisleNumber: hintLabel ? parseAisleNumberFromName(hintLabel) : undefined,
      suggestedLocationLabel: hintLabel,
    };
    (image.instoreLocationId != null ? pendingSuggestions : unmappedEvidence).push(item);
  }

  for (const image of mergedImages) {
    const hasCanonicalLink = Boolean(image.floorplanZoneId || image.floorplanAisleId);
    if (hasCanonicalLink) continue;
    const hintIds = image.instoreLocationIds ?? [];
    const hintLabel = hintIds.length ? locationNameById.get(hintIds[0]!) : undefined;
    const item: UnmappedEvidenceItem = {
      id: image.id,
      kind: 'gallery_image',
      title: image.caption,
      captureDate: image.captureDate,
      thumbnailUrl: image.url,
      instoreLocationIds: hintIds,
      suggestedAisleNumber: hintLabel ? parseAisleNumberFromName(hintLabel) : undefined,
      suggestedLocationLabel: hintLabel,
    };
    (hintIds.length > 0 ? pendingSuggestions : unmappedEvidence).push(item);
  }

  const storeAreasCount = countStoreAreas({
    aisles: canonicalAisles,
    floorplanZones: savedZones.length > 0 ? savedZones : (hasUploadedPlanImage ? [] : mergedFloorplanZones),
  });

  const twinReadiness = deriveTwinReadiness({
    hasStoreIdentity: Boolean(profile.storeName && profile.address && profile.retailer),
    hasFloorplanImage: Boolean(savedFloorplan?.imagePath || savedFloorplan?.imageUrl),
    aisleCount: canonicalAisles.length,
    zoneCount: savedZones.length > 0 ? savedZones.length : storeAreasCount - canonicalAisles.length,
    evidenceCount: mergedWalkthroughs.length + mergedImages.length + liveReferenceImages.length,
    pendingSuggestionCount: pendingSuggestions.length,
    unmappedEvidenceCount: unmappedEvidence.length,
    twinPublishedAt: savedFloorplan?.twinPublishedAt ?? null,
  });

  const storeInstoreLocationIds = resolveApplicableInstoreLocationIds(
    savedOverview?.instoreLocationIds ?? [],
    liveCampaigns,
  );
  const storeInstoreLocations = resolveStoreInstoreLocations(storeInstoreLocationIds, retailerLocations);

  const evidenceMappingStats = computeEvidenceMappingStats({
    ...profile,
    walkthroughs: mergedWalkthroughs,
    images: mergedImages,
    referenceImages: liveReferenceImages,
    unmappedEvidence,
    pendingSuggestions,
  });

  return {
    ...profile,
    ...mergedProfileBase,
    aisles: canonicalAisles,
    floorplanZones: mergedFloorplanZones,
    storeAreasCount,
    evidenceMappingStats,
    floorplanImageUrl: savedFloorplan?.imageUrl ?? null,
    floorplanImageVersion: savedFloorplan?.updatedAt ?? null,
    floorplanImageZoneTags: imageZoneTags,
    galleryImageMeta,
    retailerInstoreLocations: retailerLocations,
    storeInstoreLocationIds,
    storeInstoreLocations,
    images: mergedImages,
    aisleCount: canonicalAisles.length,
    mediaLocations: mergedMedia,
    totalMediaLocations: mediaLocationCount,
    totalMediaCapacity,
    campaigns: liveCampaigns,
    walkthroughs: mergedWalkthroughs,
    surveys: liveSurveys,
    referenceImages: liveReferenceImages,
    currentCampaignCount: activeCampaignCount,
    campaignsToDateCount,
    lastSurveyDate,
    twinReadiness,
    twinPublishedAt: savedFloorplan?.twinPublishedAt ?? null,
    twinPublishedByName: savedFloorplan?.twinPublishedByName ?? null,
    unmappedEvidence,
    pendingSuggestions,
    livePacks: liveContext.packs,
    formatOperatorAssignments: liveContext.formatOperatorAssignments ?? [],
    liveContextStats: liveContext.stats,
    dataSource: profile.isMockData ? 'hybrid' : 'live',
  };
}
